// client/src/intro-exit/Exit.jsx

/**
 * This file handles the multi-stage post-experiment exit evaluation and survey framework.
 * It dynamically computes final performance bonuses across evaluation rounds, presents required 
 * psychometric scales, captures participant demographics, and exposes secure Prolific registration tokens.
 */

import React, { useState, useRef, useEffect } from "react";

// The single completion code participants paste back into Prolific. It must match
// the code configured on the Prolific study. Exported because the unmatched/cancelled
// path (NoGame.jsx) shows the same code -- keep exactly one definition so the two
// screens cannot drift apart.
export const PROLIFIC_CODE = "CHQNS7MN"; //PROLIFIC CODE

// There is deliberately no FALLBACK_BASE_PAY constant here any more. Base pay is a
// property of the batch's treatment, and the server now writes it onto every player
// at creation (see noGameBasePay in server/src/callbacks.js), so no screen needs to
// keep its own copy -- a copy is exactly what drifts when the study's pay changes.

// Plausible range for a self-reported participant age. The study is an adult
// research agreement recruited through Prolific, which is 18+.
const MIN_AGE = 18;
const MAX_AGE = 100;

const MISSING_FIELDS_MESSAGE =
  "✕ Action Required: Please complete all mandatory questions on this page to continue.";
const INVALID_AGE_MESSAGE =
  `✕ Please enter a valid age between ${MIN_AGE} and ${MAX_AGE}.`;

// Whole number inside the plausible range. Rejects negatives, 0, decimals and
// exponent notation, all of which <input type="number"> otherwise lets through.
function isValidAge(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!/^\d+$/.test(trimmed)) return false;
  const age = Number(trimmed);
  return age >= MIN_AGE && age <= MAX_AGE;
}

// ==========================================
// 1. Post-Experiment Exit Survey Component
// ==========================================
export function Exit({ player, game }) {
  // Check if session storage or player state indicates completion, otherwise default to 1
  const [exitPage, setExitPage] = useState(() => {
    return sessionStorage.getItem("experiment_completed") === "true" ? 3 : 1;
  });
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationMessage, setValidationMessage] = useState(MISSING_FIELDS_MESSAGE);

  // Same scroll-container caveat as GameIntroAndQuiz: App.jsx scrolls an inner
  // `overflow-auto` div, not the window, so window.scrollTo() does nothing here.
  const topRef = useRef(null);
  const scrollToTop = (smooth = false) => {
    topRef.current?.scrollIntoView({
      block: "start",
      behavior: smooth ? "smooth" : "auto",
    });
  };

  // Runs after the new page renders rather than inside the click handler.
  useEffect(() => {
    scrollToTop();
  }, [exitPage]);
  
  const [formData, setFormData] = useState({
    feature_relied: "",
    private_interpret: "",
    second_confident: "",
    initial_helpful: "",
    chosen_helpful: "",
    ai_reliance: "",
    ai_trust: "",
    ai_frequency: "",
    advisor_update: "",
    advisor_comment: "",
    prediction_variables: "",
    age: "",
    gender: "",
    education: "",
    game_clarity: "",
    pay_fairness: "",
    ui_ease: "",
    display_issues: "",
    extra_feedback: ""
  });

  const treatment = game?.get("treatment");

  const basePay = treatment?.basePay !== undefined 
    ? parseFloat(treatment.basePay) 
    : 3.00;

  // Checks treatment config first, then pulls directly from the game's player object state
  let hasAI = false;
  if (treatment?.hasAI === true || player?.get("hasAI") === true) {
    hasAI = true;
  }

  // Read the bonus the server computed -- do not recompute it here.
  //
  // Scoring runs when each feedback stage OPENS, so by the time this page
  // renders the final round is already included and this value is complete.
  // This used to walk player.rounds and re-derive the total, which was a third
  // implementation of the same number and drifted from the server's definition
  // (it filtered the practice round on `type === "practice"`, an attribute that
  // does not exist, falling back to the roundNumber check alone).
  const accumulatedBonus =
    player?.get("accumulatedBonus") ??
    player?.participant?.get("accumulatedBonus") ??
    0.00;

  const totalPayout = basePay + accumulatedBonus;
  const prolificCode = PROLIFIC_CODE;

  const handleFieldChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  // Write the survey answers onto the player so they reach the datastore.
  // Each field is set individually rather than as one blob, so `empirica export`
  // produces one column per question. Called on every page advance as well as at
  // the end, so a participant who drops out midway still leaves their earlier
  // answers behind.
  const persistAnswers = (data) => {
    if (!player) return;
    Object.entries(data).forEach(([key, value]) => {
      player.set(key, value);
    });
  };

  const handleNextPageClick = () => {
  let keysToValidate = [];

  if (exitPage === 1) {
    // Keys present on Page 1
    keysToValidate = [
      ...(hasAI ? ["ai_reliance"] : []),
      "feature_relied", "private_interpret", "second_confident", "initial_helpful",
      "chosen_helpful", "ai_trust",
      "ai_frequency", "age", "gender", "education"
    ];
  } else if (exitPage === 2) {
    // Keys present on Page 2
    keysToValidate = ["game_clarity", "pay_fairness", "ui_ease", "display_issues"];
  }

  // Check if every mandatory key for this page is filled
  const allFilled = keysToValidate.every((key) => {
    const value = formData[key];
    return value !== undefined && String(value).trim() !== "";
  });

  if (!allFilled) {
    setValidationMessage(MISSING_FIELDS_MESSAGE);
    setShowValidationError(true);
    scrollToTop(true);
    return;
  }

  // Age needs a real range check, not just a non-empty check. <input type="number">
  // still accepts "-5", "0" and "1e3" as typed text, and this is a plain button
  // rather than a form submit, so the element's min/max are never enforced by the
  // browser. This is the only gate.
  if (exitPage === 1 && !isValidAge(formData.age)) {
    setValidationMessage(INVALID_AGE_MESSAGE);
    setShowValidationError(true);
    scrollToTop(true);
    return;
  }

  setShowValidationError(false);
  persistAnswers(formData);
  setExitPage(exitPage + 1);
};

  /**
 * Renders a 5-point scale with specific labels for each point.
 * @param {string} fieldName - The key in formData.
 * @param {Array} labels - An array of 5 strings, e.g., ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"]
 */

const renderFivePointScale = (fieldName, labels) => (
  <div className="flex items-start justify-between gap-6 pt-4 w-full">
    {labels.map((label) => {
      return (
        <label key={label} className="flex flex-col items-center gap-2 cursor-pointer flex-1">
          <input
            type="radio"
            name={fieldName}
            value={label}
            checked={formData[fieldName] === label}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="h-4 w-4 text-gray-900 border-gray-300 focus:ring-gray-900 shrink-0"
          />
          {/* Use text-center and a fixed max-width to handle long labels */}
          <span className="app-choice text-center font-sans text-gray-600 w-full max-w-[130px]">
            {label}
          </span>
        </label>
      );
    })}
  </div>
);
  // ==========================================
  // 3. Main Return Component Render
  // ==========================================
  return (
    <div ref={topRef} className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8 font-sans antialiased text-gray-900 w-full flex items-center justify-center">
      <div className="max-w-[73rem] w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        
        {/* Progress Navigation Header Wizard */}
        <div className="bg-gray-900 px-6 py-4 flex items-center justify-between text-white w-full">
          <div>
            <h1 className="app-page-title">Post-Experiment Evaluation</h1>
            <p className="app-page-subtitle text-gray-400 mt-0.5">Your responses directly advance empirical decision science research.</p>
          </div>
          <div className="app-micro-label flex items-center gap-2 font-mono bg-gray-800 px-3 py-1.5 rounded-xl border border-gray-700">
            <span className={exitPage === 1 ? "text-emerald-400" : "text-gray-500"}>Survey 1/2</span>
            <span className="text-gray-600">&bull;</span>
            <span className={exitPage === 2 ? "text-emerald-400" : "text-gray-500"}>Survey 2/2</span>
            <span className="text-gray-600">&bull;</span>
            <span className={exitPage === 3 ? "text-emerald-400" : "text-gray-500"}>Completion Code</span>
          </div>
        </div>

        <div className="p-8 sm:p-10 space-y-6">

          {/* ===================================================
          // 4. Exit Survey Page 1: Metric Evaluations Check
          =================================================== */}
          {exitPage === 1 && (
            <div className="app-body space-y-5 animate-fadeIn text-gray-700">
              <div className="border-b pb-3">
                <h2 className="app-survey-title text-gray-900 text-center">
                  Exit Survey
                </h2> 
                <p className="app-survey-note text-gray-500 text-center">
                  Note: Please finish the survey and you will find your completion code at the end.
                </p>
              </div>

              {showValidationError && (
                <div className="app-error p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl animate-pulse">
                  {validationMessage}
                </div>
              )}

              <div className="space-y-5 divide-y divide-gray-100">
                
                {/* FLOW SEGMENT 1: SYSTEM SIGNALS & DATA CLARITY */}
                <div className="space-y-1">
                  <span className="app-question text-gray-800">Which features did you rely on to make predictions, public or private? <span className="text-rose-500">*</span></span>
                  {renderFivePointScale("feature_relied", ["Only Public Info", "More on Public Info", "Both", "More on Private Info", "Only Private Info"])}
                </div>

                <div className="space-y-1 pt-4">
                  <span className="app-question text-gray-800">The private information was easy to interpret and use to make predictions. <span className="text-rose-500">*</span></span>
                  {renderFivePointScale("private_interpret", ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"])}
                </div>

                <div className="space-y-1 pt-4">
                  <span className="app-question text-gray-800">How confident did you feel about your Second Prediction overall compared to your standalone First Predictions? <span className="text-rose-500">*</span></span>
                  {renderFivePointScale("second_confident", ["Very Confident", "Somewhat Confident", "Neutral", "Somewhat Unconfident", "Not Confident"])}
                </div>
                
                {/* FLOW SEGMENT 2: PEER ADVISOR INSIGHTS */}
                <div className="space-y-1 pt-4">
                  <span className="app-question text-gray-800">The predictions shared by your Initial advisors were helpful for your Second Predictions. <span className="text-rose-500">*</span></span>
                  {renderFivePointScale("initial_helpful", ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"])}
                </div>

                <div className="space-y-1 pt-4">
                  <span className="app-question text-gray-800">The predictions shared by the advisor you chose were helpful for your Second Predictions. <span className="text-rose-500">*</span></span>
                  {renderFivePointScale("chosen_helpful", ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"])}
                </div>

                {/* FLOW SEGMENT 3: AUTOMATED AI SYSTEMS OVERVIEW */}
                {hasAI && (
                  <div className="space-y-1 pt-4">
                    <span className="app-question text-gray-800">How heavily did you rely on the AI Recommendation when making your predictions? <span className="text-rose-500">*</span></span>
                    {renderFivePointScale("ai_reliance", ["Did Not Use It", "Slightly Used", "Moderately Used", "Quite Used", "Relied Heavily"])}
                  </div>
                )}

                <div className="space-y-1 pt-4">
                  <span className="app-question text-gray-800">How much do you trust automated AI tools for complex asset evaluation or financial analytics tasks? <span className="text-rose-500">*</span></span>
                  {renderFivePointScale("ai_trust", ["No Trust", "Some Trust", "Moderate Trust", "High Trust", "Complete Trust"])}
                </div>

                <div className="space-y-2 pt-4">
                  <span className="app-question text-gray-800 block">How often do you use AI tools in your daily life? <span className="text-rose-500">*</span></span>
                  {renderFivePointScale("ai_frequency", ["Rarely", "Weekly", "Once in 2-3 days", "Daily", "Multiple times Daily"])}
                </div>

                {/* APPROACHABLE OPEN-TEXT QUESTION REPLACEMENT */}
                <div className="space-y-2 pt-4">
                  <span className="app-question text-gray-800 block">How did you decide to update your advisors? <span className="text-gray-400 font-normal">(Optional)</span></span>
                  <textarea
                    rows={3}
                    placeholder="Share any brief thoughts or observations you have..."
                    value={formData.advisor_update}
                    onChange={(e) => handleFieldChange("advisor_update", e.target.value)}
                    className="app-input w-full p-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-gray-900 transition resize-none"
                  />
                </div>

                {/* APPROACHABLE OPEN-TEXT QUESTION REPLACEMENT */}
                <div className="space-y-2 pt-4">
                  <span className="app-question text-gray-800 block">Any comment about your advisors? <span className="text-gray-400 font-normal">(Optional)</span></span>
                  <textarea
                    rows={3}
                    placeholder="Share any brief thoughts or observations you have..."
                    value={formData.advisor_comment}
                    onChange={(e) => handleFieldChange("advisor_comment", e.target.value)}
                    className="app-input w-full p-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-gray-900 transition resize-none"
                  />
                </div>
                
                {/* APPROACHABLE OPEN-TEXT QUESTION REPLACEMENT */}
                <div className="space-y-2 pt-4">
                  <span className="app-question text-gray-800 block">How did you use the variables to make predictions?<span className="text-gray-400 font-normal">(Optional)</span></span>
                  <textarea
                    rows={3}
                    placeholder="Share any brief thoughts or observations you have..."
                    value={formData.prediction_variables}
                    onChange={(e) => handleFieldChange("prediction_variables", e.target.value)}
                    className="app-input w-full p-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-gray-900 transition resize-none"
                  />
                </div>
              </div>

              {/* FLOW SEGMENT 4: PARTICIPANT PROFILE DEMOGRAPHICS */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                <div className="space-y-1.5">
                  <label className="app-question text-gray-800 block">Age <span className="text-rose-500">*</span></label>
                  <input
                    type="number"
                    min={MIN_AGE}
                    max={MAX_AGE}
                    step="1"
                    placeholder="Enter your age"
                    value={formData.age}
                    onChange={(e) => handleFieldChange("age", e.target.value)}
                    className="app-input w-full p-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-gray-900"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="app-question text-gray-800 block">Gender <span className="text-rose-500">*</span></label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleFieldChange("gender", e.target.value)}
                    className="app-input w-full p-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-gray-900"
                  >
                    <option value="">Select...</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Non-Binary">Non-Binary</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="app-question text-gray-800 block">Highest Education Level <span className="text-rose-500">*</span></label>
                  <select
                    value={formData.education}
                    onChange={(e) => handleFieldChange("education", e.target.value)}
                    className="app-input w-full p-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-gray-900"
                  >
                    <option value="">Select...</option>
                    <option value="High School or equivalent">High School or equivalent</option>
                    <option value="Some College">Some College</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree or higher">Master's Degree or higher</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t flex justify-end">
                <button
                  type="button"
                  onClick={handleNextPageClick}
                  className="app-btn px-8 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition shadow-md"
                >
                  Next Page
                </button>
              </div>
            </div>
          )}

          
          {/*// ============================================================
          // 5. Exit Survey Page 2: Compensation Token & Feedback View
          // ============================================================*/}
          {exitPage === 2 && (
            <div className="app-body space-y-5 animate-fadeIn text-gray-700">
              
              <div className="grid grid-cols-3 border border-gray-100 rounded-2xl shadow-xs overflow-hidden divide-x divide-gray-100 text-center bg-gray-50/40 w-full">
                <div className="p-4">
                  <span className="app-micro-label text-gray-400 block">Base Wage</span>
                  <strong className="app-metric-value text-gray-700 font-mono mt-0.5 block">${basePay.toFixed(2)}</strong>
                </div>
                <div className="p-4">
                  <span className="app-micro-label text-gray-400 block">Bonus Earned</span>
                  <strong className="app-metric-value text-emerald-600 font-mono mt-0.5 block">+{accumulatedBonus > 0 ? `$${accumulatedBonus.toFixed(2)}` : "$0.00"}</strong>
                </div>
                <div className="p-4 bg-gray-900 text-white border-l-2 border-gray-900">
                  <span className="app-micro-label text-gray-400 block">Total Earnings</span>
                  <strong className="app-metric-value text-emerald-400 font-mono mt-0.5 block">${totalPayout.toFixed(2)}</strong>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <h2 className="app-survey-title text-gray-900 text-center">
                  Game Feedback
                </h2>
                <p className="app-survey-note text-gray-500 text-center">
                  Note: These responses will not affect your payment. We want to understand your game experience.
                </p>

                {showValidationError && (
                <div className="app-error p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl animate-pulse">
                  {validationMessage}
                </div>
              )}

                <div className="space-y-1">
                  <span className="app-question text-gray-800">How would you rate the clarity of the game and its instructions?<span className="text-rose-500">*</span></span>
                  {renderFivePointScale("game_clarity", ["Very Unclear", "Unclear", "Neutral", "Clear", "Very Clear"])}
                </div>

                <div className="space-y-1 pt-4">
                  <span className="app-question text-gray-800">Do you feel the pay factor structure was fair?<span className="text-rose-500">*</span></span>
                  {renderFivePointScale("pay_fairness", ["Very Unfair", "Unfair", "Neutral", "Fair", "Very Fair"])}
                </div>

                <div className="space-y-1 pt-4">
                  <span className="app-question text-gray-800">How intuitive and easy to use was the user interface layout?<span className="text-rose-500">*</span></span>
                  {renderFivePointScale("ui_ease", ["Very Difficult", "Difficult", "Neutral", "Easy", "Very Easy"])}
                </div>

                <div className="space-y-2 pt-4">
                  <span className="app-question text-gray-800 block">Did you experience any freezing or visual display problems with the interface?<span className="text-rose-500">*</span></span>
                  <div className="flex gap-4">
                    {["Yes", "No"].map((item) => (
                      <label key={item} className={`app-choice px-5 py-2 border rounded-xl flex items-center gap-2 cursor-pointer transition-colors ${formData.display_issues === item ? "bg-blue-50 border-blue-400 text-blue-900" : "bg-gray-50 border-gray-200"}`}>
                        <input
                          type="radio"
                          name="display_issues"
                          value={item}
                          checked={formData.display_issues === item}
                          onChange={(e) => handleFieldChange("display_issues", e.target.value)}
                          className="h-3.5 w-3.5 text-gray-900 border-gray-300 focus:ring-gray-900"
                        />
                        <span>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 pt-4">
                  <span className="app-question text-gray-800 block">Any additional feedback, layout considerations, or observations? <span className="text-gray-400 font-normal">(Optional)</span></span>
                  <textarea
                    rows={4}
                    placeholder="Please share any optional suggestions regarding your system interaction..."
                    value={formData.extra_feedback}
                    onChange={(e) => handleFieldChange("extra_feedback", e.target.value)}
                    className="app-input w-full p-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white outline-none focus:ring-1 focus:ring-gray-900 transition resize-none"
                  />
                </div>
              </div>

              <div className="pt-4 border-t flex justify-between">
                <button 
                  type="button" 
                  onClick={() => {
                    let keysToValidate = ["game_clarity", "pay_fairness", "ui_ease", "display_issues"];
                    const allValid = keysToValidate.every((key) => {
                      const value = formData[key];
                      return value !== undefined && String(value).trim() !== "";
                    });
                    
                    if (allValid) {
                      setShowValidationError(false);
                      setValidationMessage(MISSING_FIELDS_MESSAGE);

                      persistAnswers(formData);

                      // Save completion flag so reloads don't loop back
                      sessionStorage.setItem("experiment_completed", "true");

                      // No player.exit() here. EmpiricaContext only renders this
                      // component for players whose "ended" attribute is already
                      // set -- every branch that reaches exitSteps checks it --
                      // and exit() early-returns when "ended" is set. So the call
                      // could never have any effect.
                      setExitPage(3);
                    } else {
                      setValidationMessage(MISSING_FIELDS_MESSAGE);
                      setShowValidationError(true);
                      scrollToTop(true);
                    }
                  }} 
                  className="app-btn w-full py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition shadow-md text-center"
                >
                  Complete Experiment & View Code
                </button>
              </div>
            </div>
          )}


          
          {/*// ===========================================================
          // 6. Exit Survey Page 3: Final Completion Code Display
          // ===========================================================*/}
          {exitPage === 3 && (
            <div className="app-body h-130 space-y-6 animate-fadeIn text-gray-700 max-w-[46.2rem] mx-auto">

              <div className="border-2 border-dashed border-emerald-300 rounded-2xl p-8 bg-emerald-50/30 shadow-2xs space-y-3 w-full text-center">
                <span className="app-micro-label text-emerald-700 block">Experiment Successfully Completed</span>
                <span className="app-micro-label text-gray-400 block pt-2">Your Prolific Completion Token</span>
                <strong className="text-3xl font-mono font-black text-gray-900 tracking-wider select-all block py-1">
                  {prolificCode}
                </strong>
                <p className="app-body text-gray-600 max-w-sm mx-auto pt-2">
                  Please copy this code and paste it back into Prolific to validate your submission. You can now safely close this window.
                </p>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
